import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, Spinner,
  Form, InputGroup, Modal, Badge, ButtonGroup, ListGroup
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  collection, query, where, orderBy, limit, startAfter, startAt, endAt,
  getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

/* ---------------- helpers ---------------- */
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

const studentsRef   = (sid) => collection(db, "schools", sid, "students");
const feesFixedRef  = (sid) => collection(db, "schools", sid, "fees_fixed");
const categoriesRef = (sid) => collection(db, "schools", sid, "fees_categories");

/* ---------- Debounced student search (server-assisted) ---------- */
const DEBOUNCE_MS = 250;
function StudentLookup({ schoolId, classFilter, sectionFilter, onPick }) {
  const [qText, setQText] = useState("");
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]   = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(async () => {
      const qtxt = qText.trim().toLowerCase();
      if (!qtxt) { setRows([]); return; }

      setLoading(true);
      try {
        const base = studentsRef(schoolId);
        const cls = classFilter ? [where("className", "==", String(classFilter))] : [];
        const sec = sectionFilter ? [where("section", "==", String(sectionFilter))] : [];

        // --- Primary: prefix search on nameLower with startAt/endAt ---
        // NOTE: This requires a composite index when combined with class/section filters:
        // Collection group: students
        // Fields: className ASC, section ASC, nameLower ASC
        let qName = query(
          base,
          ...cls, ...sec,
          orderBy("nameLower"),
          startAt(qtxt),
          endAt(qtxt + "\uf8ff"),
          limit(20)
        );

        const acc = new Map();
        try {
          const snap = await getDocs(qName);
          snap.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
        } catch (e) {
          // If index is missing (failed-precondition), retry WITHOUT class/section filters
          // so you still get results and can filter client-side.
          if (e?.code === "failed-precondition") {
            const qNameFallback = query(
              base,
              orderBy("nameLower"),
              startAt(qtxt),
              endAt(qtxt + "\uf8ff"),
              limit(20)
            );
            const snap = await getDocs(qNameFallback);
            snap.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
          } else {
            throw e;
          }
        }

        // --- Optional: numeric-looking queries on roll/phone ---
        // These only work reliably if rollNo/phone are stored as STRINGS consistently.
        if (/^\d+/.test(qtxt)) {
          try {
            const qRoll = query(
              base,
              ...cls, ...sec,
              orderBy("rollNo"),
              startAt(qtxt),
              endAt(qtxt + "\uf8ff"),
              limit(10)
            );
            const s2 = await getDocs(qRoll);
            s2.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
          } catch {/* ignore (index/type mismatch) */}
        }
        if (/^\d{3,}$/.test(qtxt)) {
          try {
            const qPhone = query(
              base,
              ...cls, ...sec,
              orderBy("phone"),
              startAt(qtxt),
              endAt(qtxt + "\uf8ff"),
              limit(10)
            );
            const s3 = await getDocs(qPhone);
            s3.docs.forEach(d => acc.set(d.id, { id: d.id, ...d.data() }));
          } catch {/* ignore (index/type mismatch) */}
        }

        // If we fell back and removed class/section from the query, filter locally
        let list = Array.from(acc.values());
        if (acc.size && (classFilter || sectionFilter)) {
          list = list.filter(st =>
            (!classFilter  || String(st.className || "") === String(classFilter)) &&
            (!sectionFilter|| String(st.section   || "") === String(sectionFilter))
          );
        }

        setRows(list.slice(0, 20));
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }, [qText, schoolId, classFilter, sectionFilter]);

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <Form.Label>Student</Form.Label>
      <InputGroup>
        <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
        <Form.Control
          placeholder="Type name, roll no, or phone…"
          value={qText}
          onChange={(e) => setQText(e.target.value)}
          onFocus={() => rows.length && setOpen(true)}
        />
        {loading && <InputGroup.Text><Spinner size="sm" animation="border" /></InputGroup.Text>}
      </InputGroup>

      {open && rows.length > 0 && (
        <div
          className="shadow border bg-white rounded-3 mt-1"
          style={{ position: "absolute", zIndex: 30, left: 0, right: 0, maxHeight: 280, overflowY: "auto" }}
        >
          <ListGroup variant="flush">
            {rows.map(st => (
              <ListGroup.Item
                key={st.id}
                action
                onClick={() => { onPick(st); setOpen(false); }}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <div className="fw-semibold">{st.name || st.displayName || "Unnamed"}</div>
                  <div className="small text-muted">
                    {st.className ? `Class ${st.className}${st.section ? `-${st.section}` : ""} · ` : ""}
                    {st.rollNo ? `Roll ${st.rollNo} · ` : ""}
                    {st.phone || st.email || st.id}
                  </div>
                </div>
                {st.section && <Badge bg="light" text="dark">{st.section}</Badge>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
}


/* ========================= Main Page ========================= */
export default function FeesFix() {
  const { school } = useSchool();
  const { isSchoolAdmin } = useAuth();
  const schoolId = school?.id || null;

  // Filters
  const [classFilter, setClassFilter]     = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter]   = useState("active"); // active | inactive | all
  const [search, setSearch]               = useState("");

  // Data
  const [categories, setCategories] = useState([]);
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const PAGE_SIZE = 50;

  // Modal
  const [showModal, setShowModal] = useState(false);
  const emptyForm = {
    id: null,
    studentId: "", studentName: "", rollNo: "", className: "", section: "",
    phone: "", email: "", fatherName: "", motherName: "",
    categoryId: "", categoryName: "",
    fixedFee: "", concession: "0",
    remarks: "", status: "active",
  };
  const [form, setForm] = useState(emptyForm);

  /* ---------- Load categories ---------- */
  useEffect(() => {
    (async () => {
      if (!schoolId) return;
      try {
        const snap = await getDocs(query(categoriesRef(schoolId), orderBy("name", "asc")));
        setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { setCategories([]); }
    })();
  }, [schoolId]);

  /* ---------- Build base query with filters ---------- */
  const buildQuery = (startAfterDoc = null) => {
    if (!schoolId) return null;

    let qArr = [ orderBy("createdAt", "desc") ];

    if (classFilter)   qArr.unshift(where("className", "==", String(classFilter)));
    if (sectionFilter) qArr.unshift(where("section", "==", String(sectionFilter)));

    if (statusFilter !== "all") qArr.unshift(where("status", "==", statusFilter));
    if (categoryFilter !== "all") qArr.unshift(where("categoryId", "==", categoryFilter));

    const q1 = query(feesFixedRef(schoolId), ...qArr, limit(PAGE_SIZE));
    if (!startAfterDoc) return q1;
    return query(feesFixedRef(schoolId), ...qArr, startAfter(startAfterDoc), limit(PAGE_SIZE));
  };

  /* ---------- Initial fetch / refetch when filters change ---------- */
  useEffect(() => {
    (async () => {
      if (!schoolId) return;
      setLoading(true);
      try {
        const baseQ = buildQuery();
        const snap = await getDocs(baseQ);
        setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setNextCursor(snap.docs.length === PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, classFilter, sectionFilter, categoryFilter, statusFilter]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoading(true);
    try {
      const q2 = buildQuery(nextCursor);
      const snap = await getDocs(q2);
      setRows(prev => [...prev, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))]);
      setNextCursor(snap.docs.length === PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null);
    } finally { setLoading(false); }
  };

  /* ---------- Client-side search filter ---------- */
  const viewRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      String(r.studentName || "").toLowerCase().includes(s) ||
      String(r.rollNo || "").toLowerCase().includes(s) ||
      String(r.phone || "").toLowerCase().includes(s) ||
      String(r.email || "").toLowerCase().includes(s) ||
      String(r.className || "").toLowerCase().includes(s) ||
      String(r.section || "").toLowerCase().includes(s) ||
      String(r.categoryName || "").toLowerCase().includes(s)
    );
  }, [rows, search]);

  /* ---------- Actions ---------- */
  const onOpenAdd = () => { setForm({ ...emptyForm }); setShowModal(true); };
  const onOpenEdit = (row) => {
    setForm({
      id: row.id,
      studentId: row.studentId || "",
      studentName: row.studentName || "",
      rollNo: row.rollNo || "",
      className: row.className || "",
      section: row.section || "",
      phone: row.phone || "",
      email: row.email || "",
      fatherName: row.fatherName || "",
      motherName: row.motherName || "",
      categoryId: row.categoryId || "",
      categoryName: row.categoryName || "",
      fixedFee: String(row.fixedFee ?? ""),
      concession: String(row.concession ?? "0"),
      remarks: row.remarks || "",
      status: row.status || "active",
    });
    setShowModal(true);
  };

  const onDelete = async (row) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this fixed fee row?",
      text: `${row.studentName || "Student"} – ${row.categoryName || "Category"} – ${fmtINR(row.fixedFee || 0)}`,
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteDoc(doc(db, "schools", schoolId, "fees_fixed", row.id));
      setRows(prev => prev.filter(r => r.id !== row.id));
      Swal.fire({ icon: "success", title: "Deleted" });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not delete", text: e?.message || "Try again." });
    }
  };

  const onToggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await updateDoc(doc(db, "schools", schoolId, "fees_fixed", row.id), {
        status: next, updatedAt: serverTimestamp(),
      });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: next } : r));
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not update status", text: e?.message || "Try again." });
    }
  };

  const onStudentPick = (st) => {
    setForm(f => ({
      ...f,
      studentId: st.id,
      studentName: st.name || st.displayName || "",
      rollNo: st.rollNo || st.roll || "",
      className: st.className || st.class || "",
      section: st.section || "",
      phone: st.phone || "",
      email: st.email || "",
      fatherName: st.fatherName || st.parentFather || "",
      motherName: st.motherName || st.parentMother || "",
    }));
  };
const cleanDoc = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && !(typeof v === "number" && Number.isNaN(v))
    )
  );
 const onSave = async () => {
  if (!schoolId) return;

  const fixedFee   = Number(form.fixedFee);
  const concession = Number(form.concession || 0);

  if (!form.studentId)
    return Swal.fire({ icon: "warning", title: "Select a student" });
  if (!Number.isFinite(fixedFee) || fixedFee < 0)
    return Swal.fire({ icon: "warning", title: "Enter a valid Fixed Fee" });
  if (!Number.isFinite(concession) || concession < 0)
    return Swal.fire({ icon: "warning", title: "Enter a valid Concession" });

  // Resolve category display name
  const cName = (form.categoryId && (categories.find(c => c.id === form.categoryId)?.name)) ||
                (form.categoryName || "");

  // Never put undefined into Firestore. Use "" or null for empties,
  // and then strip undefined/NaN with cleanDoc.
  const basePayload = {
    studentId:   form.studentId,
    studentName: (form.studentName ?? "").trim(),
    rollNo:      String(form.rollNo ?? "").trim(),
    className:   String(form.className ?? "").trim(),
    section:     String(form.section ?? "").trim(),
    phone:       String(form.phone ?? "").trim(),
    email:       String(form.email ?? "").trim(),
    fatherName:  String(form.fatherName ?? "").trim(),
    motherName:  String(form.motherName ?? "").trim(),

    // Keep id and name separate; do NOT fallback id to name.
    categoryId:   form.categoryId || "",      // or null if you prefer
    categoryName: cName || "",                // or null if you prefer

    fixedFee,
    concession,
    netAmount: Math.max(0, fixedFee - concession),

    // IMPORTANT: do not allow undefined here
    remarks: (form.remarks ?? "").trim(),

    status: form.status || "active",
    updatedAt: serverTimestamp(),
    ...(form.id ? {} : { createdAt: serverTimestamp() }),
  };

  const payload = cleanDoc(basePayload);

  try {
    if (form.id) {
      await updateDoc(doc(db, "schools", schoolId, "fees_fixed", form.id), payload);
      setRows(prev => prev.map(r => r.id === form.id ? { ...r, ...payload } : r));
    } else {
      const ref = await addDoc(feesFixedRef(schoolId), payload);
      setRows(prev => [{ id: ref.id, ...payload }, ...prev]);
    }
    setShowModal(false);
    Swal.fire({ icon: "success", title: "Saved" });
  } catch (e) {
    Swal.fire({ icon: "error", title: "Could not save", text: e?.message || "Try again." });
  }
};


  const exportCSV = () => {
    const head = [
      "Student","Roll","Class","Section","Phone","Email","Father","Mother",
      "Category","Fixed Fee","Concession","Net Amount","Status","Remarks","Created At","Updated At"
    ];
    const body = rows.map(r => [
      r.studentName || "", r.rollNo || "", r.className || "", r.section || "",
      r.phone || "", r.email || "", r.fatherName || "", r.motherName || "",
      r.categoryName || "", r.fixedFee || 0, r.concession || 0, r.netAmount || Math.max(0,(r.fixedFee||0)-(r.concession||0)),
      r.status || "active", r.remarks || "",
      r.createdAt?.toDate ? r.createdAt.toDate().toISOString() : "",
      r.updatedAt?.toDate ? r.updatedAt.toDate().toISOString() : "",
    ]);
    const csv = [head, ...body].map(a => a.map(x => `"${String(x).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fees_fixed_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!isSchoolAdmin) {
    return (
      <Container className="py-4">
        <Card className="border-0 shadow-sm"><Card.Body className="py-5 text-center">
          <h5>Restricted</h5><div className="text-muted">Only School Admins can access this page.</div>
        </Card.Body></Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="mb-1">Fix Student Fees</h5>
          <div className="text-muted small">{school?.name ? <>School: <b>{school.name}</b></> : "School selected"}</div>
        </Col>
        <Col xs="auto" className="d-flex flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={exportCSV}>Export CSV</Button>
          <Button onClick={onOpenAdd}><i className="bi bi-plus-lg me-1" />Add Fixed Fee</Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label className="text-muted">Class</Form.Label>
              <Form.Control placeholder="e.g., 10" value={classFilter} onChange={(e)=>setClassFilter(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="text-muted">Section</Form.Label>
              <Form.Control placeholder="e.g., A" value={sectionFilter} onChange={(e)=>setSectionFilter(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label className="text-muted">Category</Form.Label>
              <Form.Select value={categoryFilter} onChange={(e)=>setCategoryFilter(e.target.value)}>
                <option value="all">All</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="text-muted">Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="text-muted">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Name / Roll / Phone / Email / Category"
                  value={search}
                  onChange={(e)=>setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 fw-semibold">Fixed Fees</Card.Header>
        <Card.Body className="p-0">
          {loading && rows.length === 0 ? (
            <div className="text-center text-muted py-5">
              <Spinner animation="border" /> Loading…
            </div>
          ) : viewRows.length === 0 ? (
            <div className="text-muted text-center py-5">No records. Click <b>Add Fixed Fee</b> to create one.</div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Father</th>
                    <th>Mother</th>
                    <th>Category</th>
                    <th className="text-end">Fixed</th>
                    <th className="text-end">Concession</th>
                    <th className="text-end">Net</th>
                    <th>Status</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {viewRows.map(r => {
                    const net = Number.isFinite(r.netAmount) ? r.netAmount : Math.max(0, (r.fixedFee || 0) - (r.concession || 0));
                    return (
                      <tr key={r.id}>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {r.studentName || "—"} {r.rollNo ? <span className="text-muted">• Roll {r.rollNo}</span> : null}
                        </td>
                        <td>{r.className || "—"}{r.section ? `-${r.section}` : ""}</td>
                        <td>{r.phone || "—"}</td>
                        <td className="text-truncate" style={{ maxWidth: 180 }}>{r.email || "—"}</td>
                        <td className="text-truncate" style={{ maxWidth: 140 }}>{r.fatherName || "—"}</td>
                        <td className="text-truncate" style={{ maxWidth: 140 }}>{r.motherName || "—"}</td>
                        <td>{r.categoryName || "—"}</td>
                        <td className="text-end">{fmtINR(r.fixedFee || 0)}</td>
                        <td className="text-end">{fmtINR(r.concession || 0)}</td>
                        <td className="text-end fw-semibold">{fmtINR(net)}</td>
                        <td><Badge bg={r.status === "active" ? "success" : "secondary"}>{r.status || "active"}</Badge></td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-secondary" onClick={()=>onOpenEdit(r)}>Edit</Button>
                            <Button size="sm" variant="outline-warning" onClick={()=>onToggleStatus(r)} title="Toggle active/inactive">
                              {r.status === "active" ? "Deactivate" : "Activate"}
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={()=>onDelete(r)}><i className="bi bi-trash" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="p-3 d-flex justify-content-center">
                {nextCursor ? (
                  <Button variant="outline-primary" onClick={loadMore} disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : "Load more"}
                  </Button>
                ) : (
                  <div className="text-muted small">End of list</div>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ADD/EDIT MODAL */}
      <Modal show={showModal} onHide={()=>setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? "Edit Fixed Fee" : "Add Fixed Fee"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e)=>{ e.preventDefault(); onSave(); }}>
            {/* Student finder */}
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="text-muted">Class (filter)</Form.Label>
                <Form.Control value={classFilter} onChange={(e)=>setClassFilter(e.target.value)} placeholder="e.g., 10" />
              </Col>
              <Col md={6}>
                <Form.Label className="text-muted">Section (filter)</Form.Label>
                <Form.Control value={sectionFilter} onChange={(e)=>setSectionFilter(e.target.value)} placeholder="e.g., A" />
              </Col>
            </Row>
            <div className="mt-2" />
            <StudentLookup schoolId={schoolId} classFilter={classFilter} sectionFilter={sectionFilter} onPick={onStudentPick} />
            <div className="form-text mb-2">Select a student to auto-fill details.</div>

            <Row className="g-2">
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Student Name</Form.Label><Form.Control value={form.studentName} onChange={(e)=>setForm(f=>({...f, studentName:e.target.value}))} /></Form.Group></Col>
              <Col md={3}><Form.Group className="mb-2"><Form.Label>Class</Form.Label><Form.Control value={form.className} onChange={(e)=>setForm(f=>({...f, className:e.target.value}))} /></Form.Group></Col>
              <Col md={3}><Form.Group className="mb-2"><Form.Label>Section</Form.Label><Form.Control value={form.section} onChange={(e)=>setForm(f=>({...f, section:e.target.value}))} /></Form.Group></Col>
            </Row>

            <Row className="g-2">
              <Col md={3}><Form.Group className="mb-2"><Form.Label>Roll No</Form.Label><Form.Control value={form.rollNo} onChange={(e)=>setForm(f=>({...f, rollNo:e.target.value}))} /></Form.Group></Col>
              <Col md={5}><Form.Group className="mb-2"><Form.Label>Phone</Form.Label><Form.Control value={form.phone} onChange={(e)=>setForm(f=>({...f, phone:e.target.value}))} /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Email</Form.Label><Form.Control type="email" value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} /></Form.Group></Col>
            </Row>

            <Row className="g-2">
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Father Name</Form.Label><Form.Control value={form.fatherName} onChange={(e)=>setForm(f=>({...f, fatherName:e.target.value}))} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Mother Name</Form.Label><Form.Control value={form.motherName} onChange={(e)=>setForm(f=>({...f, motherName:e.target.value}))} /></Form.Group></Col>
            </Row>

            <Row className="g-2">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e)=>{
                      const v = e.target.value;
                      setForm(f => ({
                        ...f,
                        categoryId: v,
                        categoryName: v && v !== "custom"
                          ? (categories.find(c => c.id === v)?.name || "")
                          : f.categoryName
                      }));
                    }}
                  >
                    <option value="">— Select —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                    <option value="custom">+ Custom (type below)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}><Form.Group className="mb-2"><Form.Label>Custom Category</Form.Label><Form.Control value={form.categoryName} onChange={(e)=>setForm(f=>({...f, categoryName: e.target.value}))} placeholder="e.g., Tuition / Transport…" /></Form.Group></Col>
            </Row>

            <Row className="g-2">
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Fixed Fee (₹)</Form.Label><Form.Control type="number" value={form.fixedFee} onChange={(e)=>setForm(f=>({...f, fixedFee:e.target.value}))} required /></Form.Group></Col>
              <Col md={4}><Form.Group className="mb-2"><Form.Label>Concession (₹)</Form.Label><Form.Control type="number" value={form.concession} onChange={(e)=>setForm(f=>({...f, concession:e.target.value}))} /></Form.Group></Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={form.status} onChange={(e)=>setForm(f=>({...f, status:e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label>Remarks (optional)</Form.Label>
              <Form.Control value={form.remarks} onChange={(e)=>setForm(f=>({...f, remarks:e.target.value}))} placeholder="Concession reason / notes" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={()=>setShowModal(false)}>Cancel</Button>
          <Button onClick={onSave}>{form.id ? "Save Changes" : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
